import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import StoryCreator from './StoryCreator';
import StoryViewer from './StoryViewer';

const Avatar = ({ src, name, size = 56, ring = 'unseen' }) => {
  const ringClass =
    ring === 'unseen'
      ? 'p-[2px] bg-gradient-to-tr from-purple-500 via-fuchsia-500 to-pink-500'
      : ring === 'seen'
      ? 'p-[2px] bg-gray-200'
      : 'p-0';
  return (
    <div className={`rounded-full ${ringClass}`} style={{ width: size + 4, height: size + 4 }}>
      <div className="rounded-full p-[2px] bg-white" style={{ width: size, height: size }}>
        {src ? (
          <img
            src={src}
            alt={name || ''}
            className="rounded-full object-cover w-full h-full"
          />
        ) : (
          <div
            className="rounded-full w-full h-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold"
            style={{ fontSize: size * 0.35 }}
          >
            {(name || '?').charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
};

const StoriesCarousel = ({ token, API, t, isRTL, currentUser }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [viewerStartIdx, setViewerStartIdx] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/stories`, { headers });
      setGroups(res.data || []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => {
    load();
  }, [load]);

  const ownGroup = groups.find((g) => g.user_id === currentUser?.id) || null;
  const otherGroups = groups.filter((g) => g.user_id !== currentUser?.id);
  const ordered = ownGroup ? [ownGroup, ...otherGroups] : otherGroups;

  const openViewer = (uid) => {
    const idx = ordered.findIndex((g) => g.user_id === uid);
    if (idx >= 0) setViewerStartIdx(idx);
  };

  return (
    <div data-testid="stories-carousel" className="bg-white border-b border-gray-100">
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="max-w-xl mx-auto px-3 py-3 flex gap-3 overflow-x-auto no-scrollbar"
      >
        {/* Your story tile (always first) */}
        <button
          type="button"
          onClick={() => {
            if (ownGroup) openViewer(currentUser.id);
            else setCreatorOpen(true);
          }}
          data-testid="stories-own-tile"
          className="flex flex-col items-center gap-1 shrink-0 w-16"
        >
          <div className="relative">
            <Avatar
              src={currentUser?.profile_picture_url}
              name={currentUser?.name}
              size={56}
              ring={ownGroup ? (ownGroup.has_unseen ? 'unseen' : 'seen') : 'none'}
            />
            {!ownGroup && (
              <span
                data-testid="stories-own-add"
                className="absolute -bottom-1 -end-1 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center border-2 border-white"
              >
                <Plus className="w-3 h-3" />
              </span>
            )}
          </div>
          <span className="text-[11px] text-gray-700 truncate max-w-full">
            {ownGroup ? t('yourStory') : t('addStory')}
          </span>
        </button>

        {/* Loading / empty hint */}
        {loading && otherGroups.length === 0 && !ownGroup && (
          <div className="flex items-center text-xs text-gray-400 px-2">{t('loading')}</div>
        )}

        {/* Other users' stories */}
        {otherGroups.map((g) => (
          <button
            key={g.user_id}
            type="button"
            onClick={() => openViewer(g.user_id)}
            data-testid={`stories-tile-${g.user_id}`}
            className="flex flex-col items-center gap-1 shrink-0 w-16"
          >
            <Avatar
              src={g.profile_picture_url}
              name={g.user_name}
              size={56}
              ring={g.has_unseen ? 'unseen' : 'seen'}
            />
            <span className="text-[11px] text-gray-700 truncate max-w-full">{g.user_name}</span>
          </button>
        ))}
      </div>

      {creatorOpen && (
        <StoryCreator
          token={token}
          API={API}
          t={t}
          isRTL={isRTL}
          onClose={() => setCreatorOpen(false)}
          onCreated={load}
        />
      )}

      {viewerStartIdx !== null && (
        <StoryViewer
          token={token}
          API={API}
          t={t}
          isRTL={isRTL}
          groups={ordered}
          startUserIndex={viewerStartIdx}
          currentUserId={currentUser?.id}
          onClose={() => {
            setViewerStartIdx(null);
            load();
          }}
          onAfterChange={load}
        />
      )}
    </div>
  );
};

export default StoriesCarousel;
